import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarTodayIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
// Import axios is handled dynamically when needed

const MedicalRecordList = () => {
  const navigate = useNavigate();
  
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  useEffect(() => {
    const fetchMedicalRecords = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Import the MedicalRecordsService
        const MedicalRecordsService = (await import('../../services/MedicalRecordsService')).default;
        
        // Fetch medical records from the API
        const response = await MedicalRecordsService.getAllMedicalRecords();
        const records = response.data.map(record => {
          const patient = record?.patient ?? record?.Patient;
          const doctor = record?.doctor ?? record?.Doctor;
          return ({
            id: record?.recordId ?? record?.RecordId,
            patientName: patient ? `${patient.firstName ?? patient.FirstName ?? ''} ${patient.lastName ?? patient.LastName ?? ''}`.trim() || 'Unknown' : 'Unknown',
            patientId: record?.patientId ?? record?.PatientId,
            date: record?.visitDate ?? record?.VisitDate,
            type: record?.chiefComplaint ?? record?.ChiefComplaint ?? '', // Using chief complaint as type
            physician: doctor ? `Dr. ${doctor.lastName ?? doctor.LastName ?? ''}`.trim() || 'Unknown' : 'Unknown', // Using doctor property from backend but displaying as physician
            diagnosis: record?.diagnosis ?? record?.Diagnosis ?? '',
            notes: record?.notes ?? record?.Notes ?? '',
            tcmSyndrome: record?.syndromePattern ?? record?.SyndromePattern ?? ''
          });
        });
        
        setMedicalRecords(records);
        setFilteredRecords(records);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching medical records:', error);
        setError('Failed to load medical records. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchMedicalRecords();
  }, []);
  
  useEffect(() => {
    // Filter medical records based on search term, type, and date
    let filtered = medicalRecords;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(record => 
        record.patientName.toLowerCase().includes(term) ||
        record.physician.toLowerCase().includes(term) ||
        record.diagnosis.toLowerCase().includes(term) ||
        record.type.toLowerCase().includes(term) ||
        (record.tcmSyndrome && record.tcmSyndrome.toLowerCase().includes(term))
      );
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(record => record.type === typeFilter);
    }
    
    if (dateFilter) {
      const filterDate = new Date(dateFilter);
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.date);
        return (
          recordDate.getDate() === filterDate.getDate() &&
          recordDate.getMonth() === filterDate.getMonth() &&
          recordDate.getFullYear() === filterDate.getFullYear()
        );
      });
    }
    
    setFilteredRecords(filtered);
    setPage(0); // Reset to first page when filtering
  }, [searchTerm, typeFilter, dateFilter, medicalRecords]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  const clearSearch = () => {
    setSearchTerm('');
  };
  
  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
  };
  
  const handleDateFilterChange = (date) => {
    setDateFilter(date);
  };
  
  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
    setDateFilter(null);
  };
  
  const handleViewRecord = (recordId) => {
    navigate(`/medical-records/${recordId}`);
  };
  
  const handleEditRecord = (recordId) => {
    navigate(`/medical-records/${recordId}/edit`);
  };
  
  const handleDeleteRecord = async (recordId) => {
    if (window.confirm('Are you sure you want to delete this medical record?')) {
      try {
        // Import the MedicalRecordsService
        const MedicalRecordsService = (await import('../../services/MedicalRecordsService')).default;
        
        // Call the API to delete the record
        await MedicalRecordsService.deleteMedicalRecord(recordId);
        
        // Update the local state after successful deletion
        setMedicalRecords(medicalRecords.filter(record => record.id !== recordId));
        setFilteredRecords(filteredRecords.filter(record => record.id !== recordId));
        
      } catch (error) {
        console.error('Error deleting medical record:', error);
        alert('Failed to delete medical record. Please try again.');
      }
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  // Get unique record types for filter dropdown
  const recordTypes = ['all', ...new Set(medicalRecords.map(record => record.type))];
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Medical Records
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/medical-records/new')}
        >
          New Record
        </Button>
      </Box>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by patient, physician, diagnosis..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="type-filter-label">Record Type</InputLabel>
                <Select
                  labelId="type-filter-label"
                  value={typeFilter}
                  label="Record Type"
                  onChange={handleTypeFilterChange}
                >
                  {recordTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type === 'all' ? 'All Types' : type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Filter by Date"
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined'
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                disabled={!searchTerm && typeFilter === 'all' && !dateFilter}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="medical records table">
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Physician</TableCell>
                <TableCell>Diagnosis</TableCell>
                <TableCell>TCM Syndrome</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No medical records found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRecords
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>
                        <Button
                          sx={{ p: 0, textTransform: 'none', justifyContent: 'flex-start' }}
                          onClick={() => navigate(`/patients/${record.patientId}`)}
                        >
                          {record.patientName}
                        </Button>
                      </TableCell>
                      <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                      <TableCell>{record.type}</TableCell>
                      <TableCell>{record.physician}</TableCell>
                      <TableCell>{record.diagnosis}</TableCell>
                      <TableCell>{record.tcmSyndrome || '-'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleViewRecord(record.id)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditRecord(record.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteRecord(record.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default MedicalRecordList;
